import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, GitPullRequestArrow, Users, BookOpen, MessageSquare,
  Bell, UserCircle, Search, LogOut, Plus, ChevronDown, LifeBuoy, TrendingUp,
  ClipboardList, X, Menu,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/components/ui-kit";
import { PortalPWA } from "@/components/PortalPWA";
import api from "@/lib/api";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", exact: true, key: "dashboard" },
  { to: "/referrals", icon: GitPullRequestArrow, label: "Treatment Orders", key: "referrals" },
  { to: "/patients", icon: Users, label: "Patients", key: "patients" },
  { to: "/outcomes", icon: TrendingUp, label: "Outcomes", key: "outcomes" },
  { to: "/protocols", icon: BookOpen, label: "Protocols", key: "protocols" },
  { to: "/messages", icon: MessageSquare, label: "Messages", key: "messages", badge: "conv" },
  { to: "/alerts", icon: Bell, label: "Alerts", key: "alerts", badge: "alerts", red: true },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [counts, setCounts] = useState({ conv: 0, alerts: 0 });
  const [alerts, setAlerts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [q, setQ] = useState("");
  const [sOpen, setSOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    Promise.all([api.get("/conversations"), api.get("/alerts"), api.get("/patients"), api.get("/referrals")])
      .then(([c, a, p, r]) => {
        setCounts({ conv: c.data.length, alerts: a.data.length });
        setAlerts(a.data); setPatients(p.data); setReferrals(r.data);
      })
      .catch(() => {});
    setDrawer(false);
  }, [loc.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); searchRef.current?.focus(); setSOpen(true); }
      if (e.key === "Escape") { setSOpen(false); setBellOpen(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const ql = q.trim().toLowerCase();
  const patHits = ql ? patients.filter((p) => p.name.toLowerCase().includes(ql) || (p.diagnosis || "").toLowerCase().includes(ql)).slice(0, 5) : [];
  const refHits = ql ? referrals.filter((r) => r.patient_name.toLowerCase().includes(ql) || (r.diagnosis_label || "").toLowerCase().includes(ql) || (r.ref_no || "").toLowerCase().includes(ql)).slice(0, 5) : [];
  const goSearch = (path) => { setSOpen(false); setQ(""); nav(path); };
  const KIND_DOT = { high: "bg-rose-500", warn: "bg-amber-500", info: "bg-sky-500" };

  return (
    <div className="flex min-h-screen bg-[#F4F7F6]">
      {drawer && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setDrawer(false)} data-testid="sidebar-overlay" />}
      {/* Sidebar */}
      <aside className={`w-64 shrink-0 bg-[#0A1B33] text-slate-300 flex flex-col justify-between fixed h-screen z-40 border-r border-[#DAA520]/15 transition-transform duration-200 ${drawer ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div>
          <div className="flex items-center justify-between px-6 py-5">
            <Logo height={26} variant="light" />
            <button className="lg:hidden text-slate-400 hover:text-white" onClick={() => setDrawer(false)} data-testid="sidebar-close"><X size={20} /></button>
          </div>
          <nav className="px-3 mt-2 flex flex-col gap-1" data-testid="sidebar-nav">
            {(user?.role === "staff" ? NAV.filter((n) => ["patients", "referrals"].includes(n.key)) : NAV).map((n) => (
              <NavLink key={n.key} to={n.to} end={n.exact} data-testid={`nav-${n.key}-link`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[14px] font-medium transition-all border-l-2 ${
                    isActive ? "bg-gradient-to-r from-[#DAA520]/20 to-transparent text-white font-semibold border-[#DAA520]" : "text-slate-400 border-transparent hover:bg-white/5 hover:text-white"
                  }`}>
                <n.icon size={18} strokeWidth={2} className={""} />
                <span className="flex-1">{n.label}</span>
                {n.badge && counts[n.badge] > 0 && (
                  <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full ${n.red ? "bg-rose-500 text-white" : "bg-[#DAA520] text-[#0A1B33]"}`}>
                    {counts[n.badge]}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="p-3">
          <div className="bg-white/5 rounded-2xl p-4 mb-3 border border-white/5">
            <div className="flex items-center gap-2 text-white font-display font-semibold text-sm"><LifeBuoy size={16} className="text-[#DAA520]" /> Need help?</div>
            <p className="text-[12px] text-slate-400 mt-1 leading-relaxed">Visit the Help Center or contact PranIQ support.</p>
          </div>
          <NavLink to="/profile" data-testid="nav-profile-link"
            className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[14px] font-medium transition-all border-l-2 ${
              isActive ? "bg-gradient-to-r from-[#DAA520]/20 to-transparent text-white font-semibold border-[#DAA520]" : "text-slate-400 border-transparent hover:bg-white/5 hover:text-white"}`}>
            <UserCircle size={18} /> Profile
          </NavLink>
          <p className="text-[11px] text-slate-500 mt-3 px-2">© 2026 PranIQ</p>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 bg-white/90 backdrop-blur-md border-b border-slate-200 flex items-center gap-3 sm:gap-4 px-4 sm:px-7 sticky top-0 z-10">
          <button className="lg:hidden text-slate-600 hover:text-slate-900 shrink-0" onClick={() => setDrawer(true)} data-testid="sidebar-open"><Menu size={22} /></button>
          {/* Global search */}
          <div className="relative w-full max-w-md">
            <div className="flex items-center gap-2 bg-slate-100 rounded-xl px-3.5 py-2 text-slate-500">
              <Search size={17} />
              <input ref={searchRef} data-testid="global-search-input" value={q}
                onChange={(e) => { setQ(e.target.value); setSOpen(true); }}
                onFocus={() => setSOpen(true)}
                onBlur={() => setTimeout(() => setSOpen(false), 150)}
                placeholder="Search patients, treatment orders..."
                className="bg-transparent outline-none text-sm flex-1 text-slate-700" />
              <kbd className="hidden sm:inline text-[10px] font-semibold bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-400">⌘K</kbd>
            </div>
            {sOpen && ql && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden z-30" data-testid="search-results">
                {patHits.length === 0 && refHits.length === 0 && <div className="px-4 py-5 text-center text-[13px] text-slate-400">No matches for “{q}”</div>}
                {patHits.length > 0 && <div className="px-4 pt-3 pb-1 text-[10.5px] font-bold uppercase tracking-wide text-slate-400">Patients</div>}
                {patHits.map((p) => (
                  <button key={p.id} onMouseDown={() => goSearch("/patients")} data-testid={`search-patient-${p.id}`}
                    className="w-full flex items-center gap-2.5 px-4 py-2 hover:bg-slate-50 text-left">
                    <Users size={15} className="text-teal-600" /><span className="font-semibold text-[13px]">{p.name}</span>
                    <span className="text-[11.5px] text-slate-400 truncate">{p.diagnosis}</span>
                  </button>
                ))}
                {refHits.length > 0 && <div className="px-4 pt-3 pb-1 text-[10.5px] font-bold uppercase tracking-wide text-slate-400">Treatment Orders</div>}
                {refHits.map((r) => (
                  <button key={r.id} onMouseDown={() => goSearch(`/referrals/${r.id}`)} data-testid={`search-order-${r.id}`}
                    className="w-full flex items-center gap-2.5 px-4 py-2 hover:bg-slate-50 text-left">
                    <ClipboardList size={15} className="text-teal-600" /><span className="font-semibold text-[13px]">{r.patient_name}</span>
                    <span className="text-[11.5px] text-slate-400 truncate">{r.ref_no} · {r.diagnosis_label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2 sm:gap-4">
            {loc.pathname !== "/referrals/new" && (
              <button data-testid="new-referral-button" onClick={() => nav("/referrals/new")}
                className="pq-btn pq-btn-primary pq-btn-sm"><Plus size={16} /> <span className="hidden md:inline">New Treatment Order</span></button>
            )}

            {/* Bell dropdown */}
            <div className="relative">
              <button onClick={() => setBellOpen((v) => !v)} className="relative text-slate-500 hover:text-slate-800" data-testid="topbar-bell">
                <Bell size={19} />
                {counts.alerts > 0 && <span className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{counts.alerts}</span>}
              </button>
              {bellOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden z-30" data-testid="bell-dropdown">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100"><span className="font-display font-semibold text-[13px]">Notifications</span><button onClick={() => setBellOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={15} /></button></div>
                  <div className="max-h-80 overflow-y-auto">
                    {alerts.length === 0 ? <div className="px-4 py-6 text-center text-[13px] text-slate-400">You're all caught up 🎉</div> :
                      alerts.slice(0, 6).map((a) => (
                        <button key={a.id} onClick={() => { setBellOpen(false); nav("/alerts"); }} data-testid={`bell-alert-${a.id}`}
                          className="w-full flex gap-2.5 px-4 py-2.5 border-b border-slate-50 last:border-0 hover:bg-slate-50 text-left">
                          <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${KIND_DOT[a.kind] || KIND_DOT.info}`} />
                          <div className="min-w-0"><div className="font-semibold text-[12.5px] truncate">{a.title}</div><div className="text-[11.5px] text-slate-500 truncate">{a.description}</div></div>
                        </button>
                      ))}
                  </div>
                  <button onClick={() => { setBellOpen(false); nav("/alerts"); }} className="w-full text-center py-2.5 text-[12.5px] font-bold text-teal-700 hover:bg-slate-50 border-t border-slate-100">View all alerts</button>
                </div>
              )}
            </div>

            <div className="hidden sm:flex items-center gap-2.5 cursor-pointer" onClick={() => nav("/profile")} data-testid="topbar-profile">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#2A5298] to-[#0A1B33] text-white flex items-center justify-center font-bold text-[13px] font-display ring-2 ring-[#DAA520]/30">
                {initials(user?.name || "Dr")}
              </div>
              <div className="leading-tight hidden sm:block">
                <div className="text-[13.5px] font-bold text-slate-800">{user?.name}</div>
                <div className="text-[11.5px] text-slate-500">{user?.role}</div>
              </div>
              <ChevronDown size={15} className="text-slate-400" />
            </div>
            <button onClick={() => { logout(); nav("/"); }} data-testid="logout-button" className="pq-btn pq-btn-sm text-slate-500 hidden sm:inline-flex">
              <LogOut size={14} /> Log out
            </button>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-7 py-6 max-w-[1440px] w-full mx-auto"><PortalPWA portal="doctor" />{children}</main>
      </div>
    </div>
  );
}

export function PageHead({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6 flex-wrap animate-fade-in">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-slate-500 text-sm mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
